import { useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { Image as ImageIcon, Paperclip, ArrowUp, Sparkles, Volume2, VolumeX } from "lucide-react";
import { VoiceButton } from "@/components/ai/VoiceButton";
import { hasBrowserSpeechRecognition, useSttProvider } from "@/hooks/use-stt-provider";
import {
  TEXT_ONLY_CAPABILITIES,
  supportsModality,
  type CopilotCapabilities,
} from "@/hooks/use-copilot-capabilities";
import { MEDIA_LIMITS } from "@/lib/media-limits";
import { MEDIA_MESSAGES, acceptAttribute, type MediaModality } from "@/lib/media-prep";
import { cn } from "@/lib/utils";
import { FileAttachmentPreview } from "./FileAttachmentPreview";
import { QUICK_ACTIONS } from "./quick-actions";

/** The counter is metadata until the limit is close; below this share of the
 * limit it says nothing. */
const COUNTER_THRESHOLD = 0.9;

const MAX_FILES = MEDIA_LIMITS.maxAttachments;

type FilePreviewItem = {
  file: File;
  previewUrl?: string;
};

type ChatInputBarProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  /** What the tenant's Copilot accepts. A control the runtime cannot serve is
   * not rendered — never rendered disabled (governance rule 8). */
  capabilities?: CopilotCapabilities;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  className?: string;
  attachedFiles?: FilePreviewItem[];
  onFilesChange?: (files: FilePreviewItem[]) => void;
  /** Called with the composer's copy when a paperclip or pasted file does not fit the attachment limit. */
  onFilesRefused?: (notice: string) => void;
  isVoiceMode?: boolean;
  setIsVoiceMode?: (v: boolean) => void;
};

export type ChatInputBarRef = {
  focus: () => void;
};

export const ChatInputBar = forwardRef<ChatInputBarRef, ChatInputBarProps>(
  (
    {
      value,
      onChange,
      onSubmit,
      capabilities = TEXT_ONLY_CAPABILITIES,
      disabled = false,
      placeholder = "Como posso ajudar sua clínica hoje?",
      maxLength = 2000,
      className,
      attachedFiles = [],
      onFilesChange,
      onFilesRefused,
      isVoiceMode,
      setIsVoiceMode,
    },
    ref
  ) => {
    const { provider: tenantSttProvider } = useSttProvider();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);

    const acceptsImage = supportsModality(capabilities, "image");
    const attachModalities = ([
      acceptsImage ? "image" : null,
      supportsModality(capabilities, "pdf") ? "pdf" : null,
      supportsModality(capabilities, "audio") ? "audio" : null,
      supportsModality(capabilities, "text") ? "text" : null,
    ].filter(Boolean) as MediaModality[]);
    const canAttach = attachModalities.length > 0;
    const canDictate = hasBrowserSpeechRecognition() || capabilities.stt;
    // A tenant whose runtime does not transcribe dictates in the browser; the
    // reverse case is what the tenant hook already resolved.
    const sttProvider = capabilities.stt ? tenantSttProvider : "browser";

    const handlePaperclipClick = useCallback(() => {
      fileInputRef.current?.click();
    }, []);

    const handleCameraClick = useCallback(() => {
      cameraInputRef.current?.click();
    }, []);

    const handleFileInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files;
        if (!selected || !onFilesChange) return;

        const newFiles: FilePreviewItem[] = [];
        const totalAllowed = MAX_FILES - attachedFiles.length;

        for (let i = 0; i < Math.min(selected.length, totalAllowed); i++) {
          const file = selected[i];
          const previewUrl = file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : undefined;
          newFiles.push({ file, previewUrl });
        }

        onFilesChange([...attachedFiles, ...newFiles]);
        if (selected.length > totalAllowed) onFilesRefused?.(MEDIA_MESSAGES.turnTooLarge);
        // Reset input so same file can be re-selected
        e.target.value = "";
      },
      [attachedFiles, onFilesChange, onFilesRefused]
    );

    const handleRemoveFile = useCallback(
      (index: number) => {
        if (!onFilesChange) return;
        const updated = [...attachedFiles];
        const removed = updated.splice(index, 1);
        // Revoke previewUrl to free memory
        removed.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
        onFilesChange(updated);
      },
      [attachedFiles, onFilesChange]
    );

    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const items = e.clipboardData?.items;
        // Pasting a screenshot is an attachment like any other: it is only
        // offered when the tenant's Copilot reads images.
        if (!items || !onFilesChange || !acceptsImage) return;

        const imageFiles: FilePreviewItem[] = [];
        let refused = false;
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.type.startsWith("image/")) {
            const file = item.getAsFile();
            if (file) {
              const totalAllowed = MAX_FILES - attachedFiles.length - imageFiles.length;
              if (totalAllowed <= 0) {
                refused = true;
                break;
              }
              imageFiles.push({
                file,
                previewUrl: URL.createObjectURL(file),
              });
            }
          }
        }

        if (imageFiles.length > 0 || refused) e.preventDefault();
        if (imageFiles.length > 0) onFilesChange([...attachedFiles, ...imageFiles]);
        if (refused) onFilesRefused?.(MEDIA_MESSAGES.turnTooLarge);
      },
      [attachedFiles, onFilesChange, acceptsImage, onFilesRefused]
    );

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
    }));

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          if ((value ?? "").trim()) onSubmit();
        }
      },
      [value, onSubmit]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value.slice(0, maxLength);
        onChange(newValue);

        const textarea = e.target;
        textarea.style.height = "auto";
        textarea.style.height = Math.min(textarea.scrollHeight, 160) + "px";
      },
      [onChange, maxLength]
    );

    const handleSelectQuickAction = useCallback(
      (prompt: string) => {
        // A chip writes the prompt into the composer; the clinic reads it,
        // completes it if needed, and decides to send.
        onChange(prompt);
        textareaRef.current?.focus();
      },
      [onChange]
    );

    const safeValue = value ?? "";
    const charCount = safeValue.length;
    const isNearLimit = charCount > maxLength * COUNTER_THRESHOLD;
    const canSubmit = (safeValue.trim().length > 0 || attachedFiles.length > 0) && !disabled;
    // The chips are an opening move: they show while nothing has been typed.
    const showQuickActions = safeValue.trim().length === 0 && attachedFiles.length === 0;

    return (
      <div className={cn("group/input", className)}>
        {showQuickActions && (
          <QuickActionChips onSelect={handleSelectQuickAction} className="mb-2" />
        )}

        {/* Main container */}
        <div
          className={cn(
            "rounded-2xl border bg-app-surface transition-colors duration-150",
            isFocused ? "border-brand" : "border-app-border"
          )}
        >
          {isNearLimit && (
            <div className="flex items-center justify-end px-4 pt-3">
              <span className="text-[11px] font-medium tabular-nums text-app-warning">
                {charCount.toLocaleString()} / {maxLength.toLocaleString()}
              </span>
            </div>
          )}

          {/* Attached files preview */}
          {attachedFiles.length > 0 && (
            <FileAttachmentPreview
              files={attachedFiles}
              onRemove={handleRemoveFile}
              maxFiles={MAX_FILES}
            />
          )}

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={safeValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            aria-label="Mensagem para o assistente"
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className={cn(
              "w-full resize-none px-4 pt-3 pb-3 text-[15px] leading-relaxed",
              "placeholder:text-app-fg-subtle",
              "bg-transparent focus:outline-none",
              "text-app-fg-strong",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
            style={{ minHeight: "28px", maxHeight: "160px" }}
          />

          {/* Bottom toolbar */}
          <div className="flex items-center justify-between border-t border-app-border-subtle px-3 pb-3 pt-2">
            <div className="flex items-center gap-0.5" role="toolbar" aria-label="Ferramentas do chat">
              {/* C-20: "Adicionar contexto" was a button with an empty
                  handler. Attaching context is what the image and file buttons
                  below already do, and each one is rendered only when the
                  tenant's Copilot can read what it produces. */}
              {acceptsImage && (
                <ToolbarButton
                  icon={ImageIcon}
                  tooltip="Enviar imagem"
                  onClick={handleCameraClick}
                />
              )}
              {canAttach && (
                <ToolbarButton icon={Paperclip} tooltip="Anexar arquivo" onClick={handlePaperclipClick} active={attachedFiles.length > 0} />
              )}
              {canDictate && (
                <VoiceButton
                  onTranscript={(text) => onChange(value ? `${value} ${text}` : text)}
                  sttProvider={sttProvider}
                  disabled={disabled}
                />
              )}
              {capabilities.tts && isVoiceMode !== undefined && setIsVoiceMode && (
                <ToolbarButton
                  icon={isVoiceMode ? Volume2 : VolumeX}
                  tooltip={isVoiceMode ? "Desativar voz (TTS)" : "Ativar voz (TTS)"}
                  onClick={() => setIsVoiceMode(!isVoiceMode)}
                  active={isVoiceMode}
                  disabled={disabled}
                />
              )}
            </div>

            {/* Petrol is the one action of a view and the card approvals own
                it here; the composer sends in brand. */}
            <button
              type="button"
              aria-label="Enviar mensagem"
              onClick={onSubmit}
              disabled={!canSubmit}
              className={cn(
                "flex size-11 items-center justify-center rounded-lg sm:size-10",
                "bg-brand text-brand-fg transition-colors duration-150",
                "hover:bg-brand-hover",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2",
                "disabled:opacity-40 disabled:pointer-events-none"
              )}
            >
              <ArrowUp className="size-4" aria-hidden="true" />
            </button>
          </div>

          {/* Hidden inputs: the paperclip picks files, the image button opens
              the rear camera on a phone. Both land in the same attachment list
              and pass through media-prep before the turn is sent. */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={acceptAttribute(attachModalities)}
            onChange={handleFileInputChange}
            data-testid="copilot-file-input"
            className="hidden"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileInputChange}
            data-testid="copilot-camera-input"
            className="hidden"
          />
        </div>

        {/* Footer text */}
        <p className="mt-3 text-center text-[11px] font-medium tracking-tight text-app-fg-subtle">
          <Sparkles className="mr-1 inline-block size-3" aria-hidden="true" />
          CliniSharp pode cometer erros. Verifique informações clínicas importantes.
        </p>
      </div>
    );
  }
);

ChatInputBar.displayName = "ChatInputBar";

/* ─── Quick actions ─── */
function QuickActionChips({
  onSelect,
  className,
}: {
  onSelect: (prompt: string) => void;
  className?: string;
}) {
  return (
    <div className={cn("flex gap-1.5 overflow-x-auto p-1", className)} data-testid="copilot-quick-actions">
      {QUICK_ACTIONS.map((action) => (
        <button
          key={action.id}
          type="button"
          onClick={() => onSelect(action.prompt)}
          className={cn(
            "inline-flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-app-border bg-app-surface px-3 py-1.5 sm:min-h-10",
            "text-[13px] font-medium text-app-fg transition-colors duration-150",
            "hover:bg-brand-soft hover:text-brand",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          )}
        >
          <action.icon className="size-3.5" aria-hidden="true" />
          {action.label}
        </button>
      ))}
    </div>
  );
}

/* ─── Toolbar Button ─── */
type ToolbarButtonProps = {
  icon: React.ElementType;
  tooltip: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
};

function ToolbarButton({ icon: Icon, tooltip, onClick, active = false, disabled = false }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={tooltip}
      aria-label={tooltip}
      className={cn(
        "flex size-11 items-center justify-center rounded-lg transition-colors duration-150 sm:size-10",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2",
        active
          ? "bg-brand-soft text-brand"
          : "text-app-fg-muted hover:bg-app-surface-muted hover:text-app-fg-strong",
        disabled && "cursor-not-allowed opacity-40"
      )}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}
